/**
 * ドット勇者マスコット
 * 来場者画面で表示するピクセルアート風マスコットを提供します
 */

import React, { useMemo } from 'react';
import { View } from './WebPrimitives';

/** 表情種別 */
const MASCOT_MOOD = Object.freeze({
  NORMAL: 'normal',
  HAPPY: 'happy',
  FOCUS: 'focus',
  TROUBLE: 'trouble',
});

/** ポーズ種別 */
const MASCOT_POSE = Object.freeze({
  IDLE: 'idle',
  WIN: 'win',
  GUIDE: 'guide',
});

/** ポーズ別テンプレート */
const POSE_TEMPLATE_MAP = Object.freeze({
  [MASCOT_POSE.IDLE]: [
    '00000000000',
    '00001111100',
    '00013333310',
    '00132222231',
    '0013f22f231',
    '001322m2231',
    '00134444431',
    '00014444100',
    '00014884100',
    '00014884100',
    '00011001100',
    '00010000100',
  ],
  [MASCOT_POSE.WIN]: [
    '00000000010',
    '00001111111',
    '00013333310',
    '01132222231',
    '1013f22f231',
    '011322m2231',
    '00134444431',
    '00014444411',
    '00014888410',
    '00014884100',
    '00011001100',
    '00010000100',
  ],
  [MASCOT_POSE.GUIDE]: [
    '00000000000',
    '00001111100',
    '00013333310',
    '00132222231',
    '0013f22f231',
    '001322m2231',
    '00134444431',
    '00014444110',
    '00014888411',
    '00014884100',
    '00011001100',
    '00010000100',
  ],
});

/** 表情定義 */
const EXPRESSION_MAP = Object.freeze({
  [MASCOT_MOOD.NORMAL]: { eye: '6', mouth: '7' },
  [MASCOT_MOOD.HAPPY]: { eye: '9', mouth: 'u' },
  [MASCOT_MOOD.FOCUS]: { eye: 'a', mouth: 'n' },
  [MASCOT_MOOD.TROUBLE]: { eye: 'b', mouth: 'v' },
});

/** カラーパレット */
const PALETTE_MAP = Object.freeze({
  standard: {
    '1': '#13243A',
    '2': '#F3CCAB',
    '3': '#D8A92A',
    '4': '#2C7ECA',
    '6': '#1C2434',
    '7': '#C3645D',
    '8': '#214E83',
    '9': '#2C2F3A',
    a: '#1A253A',
    b: '#5A3345',
    n: '#604045',
    u: '#B75A62',
    v: '#874C63',
  },
  forest: {
    '1': '#153420',
    '2': '#F3CCAB',
    '3': '#7FB64A',
    '4': '#2F8352',
    '6': '#162E1E',
    '7': '#B85E53',
    '8': '#1F5B38',
    '9': '#25332A',
    a: '#1A3725',
    b: '#4F403A',
    n: '#61473F',
    u: '#A45C49',
    v: '#7A5146',
  },
});

/**
 * テンプレートに表情を適用する
 * @param {string[]} templateRows - ポーズテンプレート
 * @param {{ eye: string, mouth: string }} expression - 表情定義
 * @returns {string[]} 表情適用済みスプライト
 */
const resolveSpriteRows = (templateRows, expression) => {
  return templateRows.map((row) => {
    return row.replaceAll('f', expression.eye).replaceAll('m', expression.mouth);
  });
};

/**
 * ドット勇者マスコット
 * @param {Object} props - プロパティ
 * @param {'normal' | 'happy' | 'focus' | 'trouble'} [props.mood='normal'] - 表情
 * @param {'idle' | 'win' | 'guide'} [props.pose='idle'] - ポーズ
 * @param {'standard' | 'forest'} [props.variant='standard'] - カラーバリエーション
 * @param {number} [props.pixelSize=5] - 1ピクセルの表示サイズ
 * @returns {JSX.Element} ドット勇者
 */
const PixelHeroMascot = ({ mood = 'normal', pose = 'idle', variant = 'standard', pixelSize = 5 }) => {
  /** 使用するポーズテンプレート */
  const poseTemplate = POSE_TEMPLATE_MAP[pose] ?? POSE_TEMPLATE_MAP[MASCOT_POSE.IDLE];

  /** 使用する表情 */
  const expression = EXPRESSION_MAP[mood] ?? EXPRESSION_MAP[MASCOT_MOOD.NORMAL];

  /** 表情適用済みスプライト */
  const spriteRows = useMemo(() => resolveSpriteRows(poseTemplate, expression), [expression, poseTemplate]);

  /** 使用するカラーパレット */
  const palette = PALETTE_MAP[variant] ?? PALETTE_MAP.standard;

  /** スプライト幅 */
  const spriteWidth = spriteRows[0].length * pixelSize;

  /** スプライト高さ */
  const spriteHeight = spriteRows.length * pixelSize;

  return (
    <View
      accessibilityLabel="ドット勇者マスコット"
      className="rounded-none border-2 border-app-border bg-[#FFF8DD] p-2"
    >
      <View style={{ height: spriteHeight, position: 'relative', width: spriteWidth }}>
        {spriteRows.map((row, rowIndex) =>
          row.split('').map((pixelCode, columnIndex) => {
            if (pixelCode === '0') {
              return null;
            }

            const pixelColor = palette[pixelCode];

            if (!pixelColor) {
              return null;
            }

            return (
              <View
                key={`${rowIndex}-${columnIndex}`}
                style={{
                  backgroundColor: pixelColor,
                  height: pixelSize,
                  left: columnIndex * pixelSize,
                  position: 'absolute',
                  top: rowIndex * pixelSize,
                  width: pixelSize,
                }}
              />
            );
          }),
        )}
      </View>
    </View>
  );
};

export default PixelHeroMascot;
